import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, Check, Loader2, AlertCircle, Eye, Pencil } from 'lucide-react'
import { useCan } from '@/features/auth'
import { Markdown } from '@/shared/components/markdown/Markdown'
import { MarkdownEditor } from '@/shared/components/markdown/MarkdownEditor'
import { useLandingContent, useLandingContentStore, type LegalDocKey } from '@/features/admin/content/landingContent'
import { AdminContentGate } from '@/features/admin/content/AdminContentGate'

type DocKey = LegalDocKey

const DOCS: { key: DocKey; title: string; to: string }[] = [
  { key: 'terms', title: 'Términos y condiciones', to: '/terminos' },
  { key: 'privacy', title: 'Política de privacidad', to: '/policy' },
  { key: 'accountDeletion', title: 'Baja de cuenta', to: '/eliminar-cuenta' },
  { key: 'aiConnector', title: 'Conector de IA', to: '/conector-ia' },
  { key: 'aiConnectorPrivacy', title: 'Privacidad del conector', to: '/conector-ia/privacidad' },
]

export function LegalPagesPage() {
  const canEdit = useCan('landing:edit')
  const content = useLandingContent()
  const update = useLandingContentStore((s) => s.update)
  const saveSection = useLandingContentStore((s) => s.saveSection)
  const savingSection = useLandingContentStore((s) => s.savingSection)
  const dirty = useLandingContentStore((s) => s.dirty)
  const error = useLandingContentStore((s) => s.error)
  const status = useLandingContentStore((s) => s.status)
  const [active, setActive] = useState<DocKey>('terms')
  const [mobileView, setMobileView] = useState<'edit' | 'preview'>('edit')

  if (!canEdit) {
    return (
      <div className="rounded-card border border-line bg-panel p-8 text-center">
        <h2 className="text-lg font-semibold text-ink">Acceso restringido</h2>
        <p className="mt-1 text-sm text-ink-muted">Esta sección es exclusiva del administrador del portal.</p>
      </div>
    )
  }

  // Editar sin contenido real pisaría la BD con los defaults al guardar.
  if (status !== 'ready') return <AdminContentGate status={status} />

  const doc = DOCS.find((d) => d.key === active)!
  const value = content[active] ?? ''
  const isDirty = !!dirty[active]
  const isSaving = savingSection === active

  return (
    <div className="space-y-5 pb-24">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-widest text-brand">Administración del portal</span>
          <h1 className="mt-1 font-serif text-[32px] font-normal leading-[1.1] tracking-tight text-ink">
            Páginas legales
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            Edita en Markdown el contenido de las páginas centrales. Los cambios se publican al guardar.
          </p>
        </div>
        <Link
          to={doc.to}
          target="_blank"
          className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-panel px-3 py-2 text-sm font-medium text-ink hover:bg-surface"
        >
          <ExternalLink className="size-4" /> Ver página
        </Link>
      </header>

      {/* Selector de documento */}
      {/* Con varios documentos no cabe en un móvil: se desplaza dentro de su caja, no la página. */}
      <div className="inline-flex max-w-full overflow-x-auto rounded-xl border border-line bg-panel p-1">
        {DOCS.map((d) => (
          <button
            key={d.key}
            onClick={() => setActive(d.key)}
            className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              active === d.key ? 'bg-brand text-white' : 'text-ink-muted hover:text-ink'
            }`}
          >
            {d.title}
            {dirty[d.key] && <span className="size-1.5 rounded-full bg-warning" />}
          </button>
        ))}
      </div>

      {/* Conmutador editar/vista en móvil */}
      <div className="flex gap-2 lg:hidden">
        <button
          onClick={() => setMobileView('edit')}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${mobileView === 'edit' ? 'bg-ink text-white' : 'border border-line text-ink-muted'}`}
        >
          <Pencil className="size-3.5" /> Editar
        </button>
        <button
          onClick={() => setMobileView('preview')}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${mobileView === 'preview' ? 'bg-ink text-white' : 'border border-line text-ink-muted'}`}
        >
          <Eye className="size-3.5" /> Vista previa
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Editor Markdown */}
        <div className={`${mobileView === 'edit' ? 'block' : 'hidden'} lg:block`}>
          <label className="mb-1.5 block text-xs font-semibold text-ink-muted">Markdown</label>
          <MarkdownEditor
            value={value}
            onChange={(v) => update(active, v)}
            heightClass="h-[calc(60vh-2.75rem)]"
          />
        </div>

        {/* Vista previa */}
        <div className={`${mobileView === 'preview' ? 'block' : 'hidden'} lg:block`}>
          <label className="mb-1.5 block text-xs font-semibold text-ink-muted">Vista previa</label>
          <div className="h-[60vh] overflow-y-auto rounded-card border border-line bg-panel p-6">
            {value.trim() ? (
              <Markdown>{value}</Markdown>
            ) : (
              <p className="text-sm text-ink-subtle">Sin contenido todavía.</p>
            )}
          </div>
        </div>
      </div>

      {/* Barra de guardado (fija) */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-panel/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          {error ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-danger"><AlertCircle className="size-4" /> {error}</span>
          ) : isDirty ? (
            <span className="text-xs font-medium text-ink-muted">Cambios sin guardar en «{doc.title}».</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success"><Check className="size-4" /> Guardado.</span>
          )}
          <button
            onClick={() => void saveSection(active)}
            disabled={isSaving || !isDirty}
            className="ml-auto inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            {isSaving ? 'Guardando…' : 'Guardar página'}
          </button>
        </div>
      </div>
    </div>
  )
}
